import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import skillSwipeLogo from "@/assets/skillswipe-logo.png";
import QRCode from "qrcode";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Skill {
  id: string;
  skill: {
    id: string;
    name: string;
    category: string;
    emoji: string;
  };
}

interface Profile {
  name: string;
  bio?: string | null;
  avatar_url?: string | null;
  profile_image_1?: string | null;
  rating?: number | null;
  sessions_completed?: number | null;
  renaissance_score?: number | null;
  level?: string | null;
}

interface SkillResumeCardProps {
  profile: Profile;
  teachingSkills: Skill[];
  learningSkills: Skill[];
  userAvatar: string;
  userId: string;
}

export const SkillResumeCard = ({ profile, teachingSkills, learningSkills, userAvatar, userId }: SkillResumeCardProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [generating, setGenerating] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const generateResumeImage = async (): Promise<string | null> => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // Card dimensions (Instagram story size for better sharing)
      const width = 1080;
      const height = 1920;
      canvas.width = width;
      canvas.height = height;

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(0.5, '#764ba2');
      gradient.addColorStop(1, '#6B8DD6');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Add subtle pattern overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
      for (let i = 0; i < width; i += 40) {
        for (let j = 0; j < height; j += 40) {
          ctx.beginPath();
          ctx.arc(i, j, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Load and draw logo
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        logo.onload = () => resolve();
        logo.onerror = reject;
        logo.src = skillSwipeLogo;
      });
      
      const logoWidth = 280;
      const logoHeight = (logo.height / logo.width) * logoWidth;
      ctx.drawImage(logo, (width - logoWidth) / 2, 60, logoWidth, logoHeight);

      // White card container
      const cardY = logoHeight + 100;
      const cardHeight = height - cardY - 180;
      const cardMargin = 60;
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.roundRect(cardMargin, cardY, width - cardMargin * 2, cardHeight, 30);
      ctx.fill();

      // Shadow effect
      ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = 10;

      // Load and draw avatar
      const avatar = new Image();
      avatar.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        avatar.onload = () => resolve();
        avatar.onerror = () => resolve();
        avatar.src = userAvatar;
      });
      
      const avatarSize = 160;
      const avatarX = width / 2 - avatarSize / 2;
      const avatarY = cardY + 50;
      
      // Avatar circle clip
      ctx.save();
      ctx.beginPath();
      ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(avatar, avatarX, avatarY, avatarSize, avatarSize);
      ctx.restore();
      
      // Avatar border
      ctx.strokeStyle = '#667eea';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2 + 3, 0, Math.PI * 2);
      ctx.stroke();

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // User name
      ctx.fillStyle = '#1a1a2e';
      ctx.font = 'bold 56px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(profile.name || 'SkillSwipe User', width / 2, avatarY + avatarSize + 70);

      // Bio
      if (profile.bio) {
        ctx.fillStyle = '#666';
        ctx.font = '32px system-ui, -apple-system, sans-serif';
        const bioText = profile.bio.length > 50 ? profile.bio.substring(0, 50) + '...' : profile.bio;
        ctx.fillText(bioText, width / 2, avatarY + avatarSize + 120);
      }

      // Stats row
      const statsY = avatarY + avatarSize + 180;
      const statWidth = (width - cardMargin * 4) / 3;
      
      const stats = [
        { value: (profile.rating || 0).toFixed(1), label: '⭐ Rating' },
        { value: String(profile.sessions_completed || 0), label: '🎯 Sessions' },
        { value: String(profile.renaissance_score || 0), label: '🏆 Score' },
      ];

      stats.forEach((stat, i) => {
        const x = cardMargin * 2 + statWidth * i + statWidth / 2;
        
        ctx.fillStyle = '#667eea';
        ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
        ctx.fillText(stat.value, x, statsY);
        
        ctx.fillStyle = '#888';
        ctx.font = '28px system-ui, -apple-system, sans-serif';
        ctx.fillText(stat.label, x, statsY + 40);
      });

      // Divider
      ctx.strokeStyle = '#eee';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cardMargin + 40, statsY + 80);
      ctx.lineTo(width - cardMargin - 40, statsY + 80);
      ctx.stroke();

      // Skills I Can Teach
      let currentY = statsY + 140;
      
      ctx.fillStyle = '#667eea';
      ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('🎓 Skills I Can Teach', cardMargin + 40, currentY);
      
      currentY += 50;
      ctx.font = '30px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#333';
      
      if (teachingSkills.length > 0) {
        const skillsPerRow = 2;
        const skillBoxWidth = (width - cardMargin * 2 - 120) / skillsPerRow;
        
        teachingSkills.slice(0, 6).forEach((item, i) => {
          const row = Math.floor(i / skillsPerRow);
          const col = i % skillsPerRow;
          const x = cardMargin + 40 + col * skillBoxWidth;
          const y = currentY + row * 55;
          
          ctx.fillStyle = 'rgba(102, 126, 234, 0.1)';
          ctx.beginPath();
          ctx.roundRect(x, y - 30, skillBoxWidth - 20, 45, 10);
          ctx.fill();
          
          ctx.fillStyle = '#333';
          ctx.font = '28px system-ui, -apple-system, sans-serif';
          const skillText = `${item.skill.emoji} ${item.skill.name}`;
          ctx.fillText(skillText.substring(0, 20), x + 15, y);
        });
        currentY += Math.ceil(Math.min(teachingSkills.length, 6) / skillsPerRow) * 55 + 30;
      } else {
        ctx.fillStyle = '#999';
        ctx.fillText('No skills added yet', cardMargin + 40, currentY);
        currentY += 50;
      }

      // Skills I Want to Learn
      currentY += 30;
      ctx.fillStyle = '#764ba2';
      ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
      ctx.fillText('📚 Skills I Want to Learn', cardMargin + 40, currentY);
      
      currentY += 50;
      
      if (learningSkills.length > 0) {
        const skillsPerRow = 2;
        const skillBoxWidth = (width - cardMargin * 2 - 120) / skillsPerRow;
        
        learningSkills.slice(0, 6).forEach((item, i) => {
          const row = Math.floor(i / skillsPerRow);
          const col = i % skillsPerRow;
          const x = cardMargin + 40 + col * skillBoxWidth;
          const y = currentY + row * 55;
          
          ctx.fillStyle = 'rgba(118, 75, 162, 0.1)';
          ctx.beginPath();
          ctx.roundRect(x, y - 30, skillBoxWidth - 20, 45, 10);
          ctx.fill();
          
          ctx.fillStyle = '#333';
          ctx.font = '28px system-ui, -apple-system, sans-serif';
          const skillText = `${item.skill.emoji} ${item.skill.name}`;
          ctx.fillText(skillText.substring(0, 20), x + 15, y);
        });
      } else {
        ctx.fillStyle = '#999';
        ctx.fillText('No learning goals yet', cardMargin + 40, currentY);
      }

      // Generate QR code for public profile
      const profileUrl = `https://www.skillswipe.co.in/profile/${userId}`;
      const qrCodeDataUrl = await QRCode.toDataURL(profileUrl, {
        width: 160,
        margin: 1,
        color: {
          dark: '#1a1a2e',
          light: '#ffffff',
        },
        errorCorrectionLevel: 'M',
      });

      // Load and draw QR code
      const qrImage = new Image();
      await new Promise<void>((resolve) => {
        qrImage.onload = () => resolve();
        qrImage.onerror = () => resolve();
        qrImage.src = qrCodeDataUrl;
      });

      const qrSize = 140;
      const qrX = width / 2 - qrSize / 2;
      const qrY = height - 280;

      // QR code background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.roundRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30, 15);
      ctx.fill();

      // Draw QR code
      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

      // QR code label
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '22px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Scan to view profile', width / 2, qrY + qrSize + 50);

      // Footer with website
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('www.skillswipe.co.in', width / 2, height - 70);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '26px system-ui, -apple-system, sans-serif';
      ctx.fillText('Swipe. Learn. Teach. Grow.', width / 2, height - 30);

      return canvas.toDataURL('image/png', 1.0);
    } catch (error) {
      console.error('Error generating resume:', error);
      return null;
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const imageUrl = await generateResumeImage();
      if (imageUrl) {
        const link = document.createElement('a');
        link.download = `${profile.name || 'skillswipe'}-skill-resume.png`;
        link.href = imageUrl;
        link.click();
        
        toast({
          title: "Resume downloaded!",
          description: "Your skill resume has been saved as an image.",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleShare = async () => {
    setGenerating(true);
    try {
      const imageUrl = await generateResumeImage();
      if (imageUrl) {
        setGeneratedImageUrl(imageUrl);
        setShareDialogOpen(true);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const shareToWhatsApp = () => {
    const text = `Check out my SkillSwipe profile! 🎯\n\nI can teach: ${teachingSkills.slice(0, 3).map(s => s.skill.name).join(', ') || 'Various skills'}\nI want to learn: ${learningSkills.slice(0, 3).map(s => s.skill.name).join(', ') || 'New skills'}\n\nJoin me on SkillSwipe! 🚀\nwww.skillswipe.co.in`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    toast({
      title: "Opening WhatsApp",
      description: "Don't forget to attach your downloaded resume image!",
    });
  };

  const shareToTwitter = () => {
    const text = `Check out my skill resume on @SkillSwipe! 🎯\n\nI teach: ${teachingSkills.slice(0, 2).map(s => s.skill.name).join(', ') || 'skills'}\nLearning: ${learningSkills.slice(0, 2).map(s => s.skill.name).join(', ') || 'new things'}\n\nSwipe. Learn. Teach. Grow. 🚀`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent('https://www.skillswipe.co.in')}`;
    window.open(url, '_blank');
  };

  const shareToInstagram = async () => {
    // Download the image first since Instagram doesn't support direct web sharing
    if (generatedImageUrl) {
      const link = document.createElement('a');
      link.download = `${profile.name || 'skillswipe'}-skill-resume.png`;
      link.href = generatedImageUrl;
      link.click();
      
      toast({
        title: "Image downloaded!",
        description: "Open Instagram and share the image from your gallery.",
      });
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share && generatedImageUrl) {
      try {
        // Convert data URL to blob for native sharing
        const response = await fetch(generatedImageUrl);
        const blob = await response.blob();
        const file = new File([blob], `${profile.name || 'skillswipe'}-skill-resume.png`, { type: 'image/png' });
        
        await navigator.share({
          title: 'My SkillSwipe Resume',
          text: `Check out my skill resume! I teach ${teachingSkills.slice(0, 2).map(s => s.skill.name).join(', ')} and I'm learning ${learningSkills.slice(0, 2).map(s => s.skill.name).join(', ')}. Join me on SkillSwipe!`,
          url: 'https://www.skillswipe.co.in',
          files: [file],
        });
      } catch (error) {
        console.log('Native share failed, using fallback');
      }
    }
  };

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={handleDownload}
          disabled={generating}
        >
          {generating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          {generating ? 'Generating...' : 'Download Resume'}
        </Button>
        
        <Button 
          variant="outline"
          onClick={handleShare}
          disabled={generating}
          className="px-3"
        >
          <Share2 className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Your Skill Resume</DialogTitle>
            <DialogDescription>
              Share your skill resume with friends and on social media
            </DialogDescription>
          </DialogHeader>
          
          {generatedImageUrl && (
            <div className="mb-4">
              <img 
                src={generatedImageUrl} 
                alt="Skill Resume Preview" 
                className="w-full max-h-64 object-contain rounded-lg border"
              />
            </div>
          )}
          
          <div className="grid grid-cols-3 gap-3">
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-green-50 hover:border-green-500"
              onClick={shareToWhatsApp}
            >
              <svg className="h-8 w-8 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-xs font-medium">WhatsApp</span>
            </Button>
            
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-pink-50 hover:border-pink-500"
              onClick={shareToInstagram}
            >
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
                <defs>
                  <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFDC80" />
                    <stop offset="25%" stopColor="#FCAF45" />
                    <stop offset="50%" stopColor="#F77737" />
                    <stop offset="75%" stopColor="#F56040" />
                    <stop offset="100%" stopColor="#C13584" />
                  </linearGradient>
                </defs>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span className="text-xs font-medium">Instagram</span>
            </Button>
            
            <Button
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-blue-50 hover:border-blue-400"
              onClick={shareToTwitter}
            >
              <svg className="h-8 w-8 text-black" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="text-xs font-medium">X (Twitter)</span>
            </Button>
          </div>
          
          {navigator.share && (
            <Button 
              variant="secondary" 
              className="w-full mt-2"
              onClick={handleNativeShare}
            >
              <Share2 className="mr-2 h-4 w-4" />
              More sharing options
            </Button>
          )}
          
          <p className="text-xs text-muted-foreground text-center mt-2">
            Tip: For Instagram, download the image and share from your gallery
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
